import axios from 'axios'
import { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import { useToaster } from '@enterprise-ui/canvas-ui-react'
import { getBulkExportFailure } from '../../../servicesGraphQl'
import { getUserSessionToken } from '../../../store/userProfile'
import {
  mapResponse,
  UPLOAD_HISTORY_COLUMNS,
} from '../../../utilities/uploadHistoryGrid'
import { BULK_ALERT } from '../../../constants/alertConstants'
import {
  createAttributeGroups,
  generateExportExcel,
} from '../download/bulkExportUtilities'
import veItemBulkMfeConfig from '../../../config/'
import { useMain } from '../../main/useMain'
import { getLovForValidationOptions } from '../../../services/downloadService'
import store from '../../../store'
import { suppressKeyboardEvent } from '../../../resources/A11yUtills'
import { useFirefly } from '../../../utilities/useFirefly'
import { FIREFLY_EVENTS } from '../../../constants/fireFlyConstants'
import { isWithinLast24H } from '../../../utilities/uploadHistoryGrid'
import { MS_60S } from '../../../constants/gridConstants'

export const useUploadHistory = (isFileuploaded) => {
  const makeToast = useToaster()
  const { handleTrackClickEvent } = useFirefly()
  const [loading, setLoading] = useState(false)
  const [dataFetched, setDataFetched] = useState(false) // Keep track of whether the data is already fetched
  const [timestamp, setTimestamp] = useState(Date.now())
  const [autoRefresh, setAutoRefresh] = useState(false)

  //Download button state
  const defaultButtonState = {
    inProgress: false,
    disabled: true,
  }
  const [downloadButtonState, setDownloadButtonState] =
    useState(defaultButtonState)

  const gridRef = useRef(null)
  const intervalRef = useRef(null)

  const [rowData, setRowData] = useState()
  const columnDefs = UPLOAD_HISTORY_COLUMNS
  const defaultColDef = useMemo(() => {
    return {
      sortable: true,
      filter: false,
      resizable: true,
      editable: false,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      initialWidth: 140,
      minWidth: 80,
      suppressKeyboardEvent,
    }
  }, [])
  const gridOptions = useMemo(
    () => ({
      tooltipShowDelay: 0,
      maxWidth: 100,
      rowHeight: 42,
      enableCellTextSelection: true, //to enable
      pagination: true,
      paginationPageSize: 10,
      paginationPageSizeSelector: [10, 20, 50, 100],
    }),
    [],
  )

  useEffect(() => {
    // This runs every time isFileuploaded changes i.e. when file is uploaded
    if (isFileuploaded > 0) {
      updateGrid()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFileuploaded])
  const updateGrid = useCallback((eve) => {
    eve?.preventDefault()
    setDataFetched(false)
    onGridReady()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getRowId = useCallback((params) => {
    return params.data.id
  }, [])

  const { getUserProfile } = useMain()

  const hasOkStatus = (st) => st === 'PROCESSING' || st === 'UPLOADED'

  const onGridReady = useCallback(async () => {
    if (dataFetched) return // Prevent fetching if data is already fetched

    const sessionToken = getUserSessionToken(store.getState())
    try {
      setLoading(true)
      await axios
        .get(`${veItemBulkMfeConfig.bulk.url}bulk_requests`, {
          headers: {
            'x-api-key': veItemBulkMfeConfig.apiKey,
            user_id: sessionToken || '',
          },
        })
        .then((res) => {
          const responseData = mapResponse(res.data)
          setRowData(responseData)
          const filtered = responseData?.filter(
            (r) =>
              hasOkStatus(r?.request_status) && isWithinLast24H(r?.uploaded_on),
          )
          setAutoRefresh(filtered.length > 0)
        })
    } catch {
      setRowData([])
    } finally {
      setLoading(false)
      setTimestamp(Date.now())
      setDataFetched(true) // Mark data as fetched
    }
  }, [dataFetched]) // Add dependencies here if needed

  // Effect to set up the interval on mount
  // This will check every minute if a refresh is needed
  // Will check if we have some new files from last 24 hours which are still processing
  useEffect(() => {
    // initial fetch
    const filtered = rowData?.filter(
      (r) => hasOkStatus(r?.request_status) && isWithinLast24H(r?.uploaded_on),
    ) // refresh only if there are items with PROCESSING or UPLOADED status in last 24 hours
    if (filtered && filtered.length > 0) {
      intervalRef.current = setInterval(() => {
        updateGrid()
      }, MS_60S)
      // Enable auto-refresh only if there are relevant items

      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current)
      }
    }
  })

  const showToastMessage = (toastMessage, type, heading) => {
    makeToast({
      type: type ? type : 'success',
      heading: heading ? heading : 'Heading',
      message: toastMessage,
    })
  }

  const downloadFailure = async (eve, rowData) => {
    const { id, item_type, item_type_id } = rowData
    setDownloadButtonState({ ...downloadButtonState, inProgress: true })
    try {
      const [responseData, validOptionsData] = await Promise.all([
        getBulkExportFailure(id),
        getLovForValidationOptions(
          getUserProfile,
          item_type_id ? item_type_id : item_type,
        ), //Since for old items, item_type_id was not present.So can use item_type name but that will not give uda dropdowns
      ])
      const { data } = responseData
      if (data && validOptionsData) {
        const { ExcelExportResponse, errors } = data.getBulkExportFailureData
        const time = Math.floor(Date.now() / 1000)
        const fileName = `${item_type}_Rejected_${time}`
        const optionsDataForExcel = createAttributeGroups(
          ExcelExportResponse[0]?.attributeName,
          validOptionsData.data.lov_details,
        )
        if (!!ExcelExportResponse && !errors) {
          const downloaded = await generateExportExcel(
            ExcelExportResponse,
            optionsDataForExcel,
            fileName,
            'all',
            'rejected_items', //type of export - normal or rejected items
          )
          if (downloaded === 'success') {
            showToastMessage(
              BULK_ALERT.BULK_EXPORT_SUCCESS(fileName),
              'success',
              'File Download',
            )
            // To handle the case when rejected file download is successful
            handleTrackClickEvent(
              FIREFLY_EVENTS.CUSTOM_INTERACTION_KEYS.DOWNLOAD_REJECTED_ITEMS,
              JSON.stringify({
                requestId: id,
                itemType: item_type,
                itemTypeId: item_type_id,
                downloadStatus: 'success',
              }),
            )
            setDownloadButtonState({
              ...downloadButtonState,
              inProgress: false,
            })
          } else if (downloaded === 'failed') {
            showToastMessage(errors, 'error', 'Error')
            // To handle the case when rejected file download fails
            handleTrackClickEvent(
              FIREFLY_EVENTS.CUSTOM_INTERACTION_KEYS.DOWNLOAD_REJECTED_ITEMS,
              JSON.stringify({
                requestId: id,
                itemType: item_type,
                itemTypeId: item_type_id,
                downloadStatus: 'failed',
              }),
            )
          }
        } else {
          showToastMessage(errors, 'error', 'Error')
        }
      } else {
        showToastMessage(BULK_ALERT.BULK_EXPORT_ERROR, 'error', 'Error')
      }
      setDownloadButtonState(defaultButtonState)
    } catch {
      showToastMessage(BULK_ALERT.BULK_EXPORT_ERROR, 'error', 'Error')
      setDownloadButtonState({ ...downloadButtonState, inProgress: true })
    } finally {
      setDownloadButtonState({ ...downloadButtonState, inProgress: false })
    }
  }
  return {
    timestamp,
    loading,
    gridRef,
    getRowId,
    gridOptions,
    defaultColDef,
    columnDefs,
    rowData,
    onGridReady,
    updateGrid,
    downloadFailure,
    downloadButtonState,
    autoRefresh,
  }
}
